import React, { useState, useEffect, useRef } from "react";
import RichTextEditor from "react-rte";
import MediaInput from "../MediaInput";
import DialogPopup from "../DialogPopup";
function MultipleChoice({ item, updateItem, lang, handleMediaUpdate, bookId }) {
  const [answers, setAnswers] = useState(
    item.contents?.a?.length > 0
      ? item.contents.a
      : [
          { id: 0, text: { en: "" }, correct: false, media: [] },
          { id: 1, text: { en: "" }, correct: false, media: [] },
        ]
  );
  const [shuffle, setShuffle] = useState(item.shuffleAnswers ?? false); // shuffles the order of the answers as the user sees them
  const [editorState, setEditorState] = useState(
    RichTextEditor.createValueFromString(
      item.contents?.q ? item.contents?.q[lang.code] : "",
      "html"
    )
  );
  const posFeedbackRef = useRef();
  const negFeedbackRef = useRef();

  useEffect(() => {
    setEditorState(
      RichTextEditor.createValueFromString(
        item.contents?.q?.[lang.code] || "",
        "html"
      )
    );
  }, [lang]);

  function handleEditorValueChange(value) {
    // console.log("value: ", value);
    setEditorState(value);
    let temp = item;

    if (value.toString("html") === "<p><br></p>") {
      temp.contents.q[lang.code] = "";
    } else {
      temp.contents.q[lang.code] = value.toString("html");
    }
    updateItem({ ...temp });
  }

  function modifyAnswersLength(count) {
    let temp = answers.slice();

    if (count > answers.length) {
      let amountToAdd = count - answers.length;
      // if we're adding a question
      // add as many blank answers as needed
      for (let x = 0; x < amountToAdd; x++) {
        temp.push({
          id: answers.length,
          text: { en: "" },
          correct: false,
          media: [],
        });
      }
      setAnswers(temp);
    } else {
      // if we're removing one
      let amountToRemove = answers.length - count;
      for (let x = 0; x < amountToRemove; x++) {
        temp.pop();
      }
      setAnswers(temp);
    }

    updateItem({
      ...item,
      contents: {
        ...item.contents,
        a: temp,
      },
    });
  }

  function updateShuffleAnswers(value) {
    setShuffle(value);
    updateItem({
      ...item,
      shuffleAnswers: value,
    });
  }

  function updateAnswer(id, text, correct) {
    let temp = answers.map((a) => {
      if (a.id === id) {
        const tempAnswer = a.text;
        tempAnswer[lang.code] = text;

        return {
          ...a,
          text: tempAnswer,
          correct,
        };
      } else return a;
    });
    setAnswers(temp);
    updateItem({
      ...item,
      contents: {
        ...item.contents,
        a: temp,
      },
    });
  }

  function updateFeedback() {
    let temp = { ...item.contents.feedback };

    // if the corresponding text field is empty, don't even create the object key
    temp[lang.code] = {
      ...(posFeedbackRef.current.value && {
        pos: posFeedbackRef.current.value,
      }),
      ...(negFeedbackRef.current.value && {
        neg: negFeedbackRef.current.value,
      }),
    };

    updateItem({
      ...item,
      // if the resulting temp object is empty (no pos or neg feedback), send a nullish value
      contents: {
        ...item.contents,
        feedback: Object.keys(temp).length === 0 ? null : temp,
      },
    });
  }
  return (
    <div className="QM-question__contents-editor" id={item.type}>
      <div className="QM-question__main-properties">
        <div className="QM-question__question">
          <p>Question</p>

          <DialogPopup
            questionIndex={item.id}
            answerIndex={null}
            media={
              item?.contents?.media?.filter((m) => m.language === lang.code)[0]
            }
            onMediaUpdate={handleMediaUpdate}
            bookId={bookId}
          />
          <RichTextEditor
            value={editorState}
            onChange={handleEditorValueChange}
          />
        </div>
        <div className="QM-question__answer-list">
          <div className="QM-question__answer-list-label">
            <p>Answers</p>
            <div>
              <label>
                <span
                  className="title-tooltip"
                  title='Not recommended when using certain answers, like "A and C", "All of the above", etc.'
                >
                  Shuffle?
                </span>{" "}
                <input
                  type="checkbox"
                  checked={shuffle}
                  onChange={(e) => updateShuffleAnswers(e.target.checked)}
                />
              </label>
              <label>
                Count:{" "}
                <input
                  type="number"
                  min={2}
                  // max={10}
                  value={answers.length}
                  onChange={(e) => modifyAnswersLength(e.target.value)} // prevents user from typing in any value outside of range
                />
              </label>
            </div>
          </div>
          <table className="QM__answer-table">
            <tbody>
              <tr>
                <th>Text</th>
                <th>Correct?</th> <th>Media?</th>
              </tr>
              {answers.map((a, i) => (
                <tr key={i}>
                  <Answer
                    key={a.id}
                    item={item}
                    lang={lang}
                    answer={a}
                    updateAnswer={updateAnswer}
                    handleMediaUpdate={handleMediaUpdate}
                    bookId={bookId}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="QM__feedback-options">
          <p>Feedback (optional)</p>
          <label htmlFor="correct-answer-feedback">Correct: </label>
          <input
            ref={posFeedbackRef}
            id="correct-answer-feedback"
            type="text"
            placeholder={`E.g. "That's right! In fact, tardigrades can survive even in the cold vacuum of space!"`}
            value={item.contents.feedback?.[lang.code]?.pos || ""}
            onChange={updateFeedback}
          />
          <label htmlFor="incorrect-answer-feedback">Incorrect: </label>
          <input
            ref={negFeedbackRef}
            id="incorrect-answer-feedback"
            type="text"
            placeholder={`E.g. "Sorry! Remember that the dividend goes in the 'house' when doing long division."`}
            value={item.contents.feedback?.[lang.code]?.neg || ""}
            onChange={updateFeedback}
          />
        </div>
      </div>
    </div>
  );
}

function Answer({
  lang,
  answer: { id, text, correct },
  updateAnswer,
  item,
  handleMediaUpdate,
  bookId,
}) {
  const textRef = useRef();
  const correctRef = useRef();

  function handleUpdate() {
    updateAnswer(id, textRef.current.value, correctRef.current.checked);
  }

  return (
    <>
      <td className="td-answer-text">
        <input
          ref={textRef}
          type="text"
          value={text[lang.code] || ""}
          onChange={handleUpdate}
          placeholder={text.en ? `En: "${text.en}"` : ""}
        />
      </td>
      <td className="td-answer-correct">
        <div className="checkbox-wrapper">
          <input
            ref={correctRef}
            type="checkbox"
            checked={correct}
            onChange={handleUpdate}
          />
        </div>
      </td>{" "}
      <td className="td-answer-correct">
        {/* <MediaInput
          questionIndex={item.id}
          answerIndex={id}
          media={item?.contents?.a.filter((m) => m.language === lang.code)[0]} // filter by current language
          onMediaUpdate={handleMediaUpdate}
        />{" "} */}
        <DialogPopup
          questionIndex={item.id}
          answerIndex={id}
          media={item?.contents?.a.filter((m) => m.language === lang.code)[0]} // filter by current language
          onMediaUpdate={handleMediaUpdate}
          bookId={bookId}
        />
      </td>
    </>
  );
}

export default MultipleChoice;
